//@ts-nocheck
import { useState, Fragment, useRef, useEffect, useCallback } from 'react';
import { Combobox, Dialog, Transition } from '@headlessui/react';
import { Search, Command, X } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { _baseRequest, COMMON } from '../../utils/baseApi';
import { debounce } from 'lodash';
import { useSearch } from '../../context/SearchContext';
import { fetchDocument } from '../../features/job/jobSlices';
import { useAppDispatch } from '../../app/hooks';



export default function CommandKSearchJob() {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [showMore, setShowMore] = useState(false);
  const dialogRef = useRef(null);
  const searchInputRef = useRef(null);
  const { baseId, applicationId } = useParams<{ baseId: string; applicationId: string; }>();
  const [isLoading, setIsLoading] = useState(false);
  const [allActivityItem, setAllActivityItem] = useState({
    jobPost: [],
    totalJobPost: 0,
    currentPage: 1,
    hasNextPage: false,
    limit: 25,
    totalPages: 1
  });
  const [searchItems, setSearchItems] = useState([]);
  const navigate = useNavigate();
  const [isDebouncedLoading, setIsDebouncedLoading] = useState(false);
  const { setSearchTerm } = useSearch();
  const dispatch = useAppDispatch();
  const getStageName = (stageId: any) => {
    // Get scorecard data from localStorage
    const scorecardData = JSON.parse(localStorage.getItem("jobPipelineStageData") || "{}");
    const stages = scorecardData?.stages || [];

    // Find the stage with matching ID
    const stage = stages.find((stage: any) => stage.id === stageId);
    return stage ? stage.stage_name : null;
  };

  const getStageColor = (stageId: any) => {
    // Get scorecard data from localStorage
    const scorecardData = JSON.parse(localStorage.getItem("jobPipelineStageData") || "{}");
    const stages = scorecardData?.stages || [];

    // Find the stage with matching ID
    const stage = stages.find((stage: any) => stage.id === stageId);
    return stage ? stage.stage_colour : null;
  }

  // Filter items based on query
  const filteredItems = searchItems.filter(item => {
    // Check if properties exist before calling toLowerCase()
    return (
      (item.label && item.label.toLowerCase().includes(query.toLowerCase())) ||
      (item.refId && item.refId.toLowerCase().includes(query.toLowerCase())) ||
      (item.experience_level && item.experience_level.toLowerCase().includes(query.toLowerCase())) ||
      (item.job_type && item.job_type.toLowerCase().includes(query.toLowerCase())) ||
      (item.status && item.status.toLowerCase().includes(query.toLowerCase()))
    );
  });
  // Only show first 3 items unless showMore is clicked
  const visibleItems = query ? filteredItems : (showMore ? searchItems : searchItems.slice(0, 3));


  const debouncedFetchSearchResults = useCallback(
    debounce((queryValue: string) => {
      setIsDebouncedLoading(true);
      fetchSearchResults(queryValue).finally(() => {
        setIsDebouncedLoading(false);
      });
    }, 400),
    [baseId, allActivityItem.limit]
  );

  // Modify fetchSearchResults to accept query as parameter
  const fetchSearchResults = useCallback(async (queryValue: string) => {
    if (!baseId) return;

    const storedData = localStorage.getItem('cachedJobsListData');
    let applications = [];

    // if (storedData) {
    //   try {
    //     const parsedData = JSON.parse(storedData);
    //     applications = parsedData.jobPost || parsedData.data || [];

    //     if (applications.length > 0 && !queryValue) {
    //       const formattedItems = applications.map(app => {
    //         const stageName = getStageName(app.stage_id);
    //         const stageColor = getStageColor(app.stage_id);
    //         return {
    //           id: app.id || `app-${Math.random()}`,
    //           label: app.app_name,
    //           description: app.app_email || app.app_headline,
    //           tags: [stageName],
    //           stageColor: stageColor,
    //           data: app
    //         };
    //       });
    //       setSearchItems(formattedItems);
    //       setAllActivityItem(prev => ({
    //         ...prev,
    //         jobPost: applications
    //       }));
    //       return;
    //     }
    //   } catch (error) {
    //     console.error("Error parsing cached data:", error);
    //   }
    // }

    let fullUrl = `${COMMON.apiBaseUrl()}/v1/${baseId}/jobPost.list?page=1&limit=${allActivityItem.limit}`;
    if (queryValue) {
      fullUrl += `&search=${queryValue}`;
    }

    try {
      setIsLoading(true);
      const response = await _baseRequest(fullUrl, 'GET');

      if (response?.data?.jobPost?.length > 0) {
        setAllActivityItem(response.data);
        const formattedItems = response.data.jobPost.map(app => {

          const stageName = getStageName(app.stage_id);
          const stageColor = getStageColor(app.stage_id);
          return {
            id: app.id,
            label: app.job_title,
            refId: app.job_ref_id,
            experience_level: app.experience_level,
            job_type: app.job_type,
            status: app.job_status,
            data: app
          };
        });
        setSearchItems(formattedItems);
      } else {
        setAllActivityItem({
          jobPost: [],
          totalJobPost: 0,
          currentPage: 1,
          hasNextPage: false,
          limit: 25,
          totalPages: 1
        });
        setSearchItems([]);
      }
    } catch (error) {
      console.error("Error fetching search results:", error);
    } finally {
      setIsLoading(false);
    }
  }, [baseId, allActivityItem.limit, getStageName, getStageColor]);

  // Update the query change handler
  const handleQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newQuery = e.target.value;
    setQuery(newQuery);
    debouncedFetchSearchResults(newQuery);
  };


  function openModal() {
    setIsOpen(true);
    setIsLoading(true);
    fetchSearchResults('').finally(() => {
      setIsLoading(false);
    });
  }

  const closeModal = () => {
    setIsOpen(false);
    setQuery('');
    setShowMore(false);
  };

  // Handle Enter key press for search
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && isOpen) {
      e.preventDefault();
      fetchSearchResults();
    }
  };


  // Handle click outside to close modal
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dialogRef.current && !dialogRef.current.contains(event.target)) {
        closeModal();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      if (searchInputRef.current) {
        setTimeout(() => {
          searchInputRef.current.focus();
        }, 10);
      }
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Handle Command+K shortcut to open modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        openModal();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const handleNavigate = async (item: any) => {
    closeModal();
    const { data } = item;
    navigate(`/${baseId}/job/${item?.id}/dashboard`)
    localStorage.removeItem('jobActionData')
    localStorage.removeItem('jobByReviewData')
    localStorage.removeItem('jobPipelineStageData')
    localStorage.removeItem('jobPostDetailsData')
    localStorage.removeItem('jobByHiringTeamMembers')
    localStorage.removeItem('activeFiltersJobApplication')
    localStorage.removeItem('activeFiltersJobApplicationKanban')
    localStorage.removeItem('activeFiltersJobReports')
    localStorage.removeItem('cachedJobsDetailsListData')
    localStorage.removeItem('baseScoreCardDataByJob')
    localStorage.removeItem('baseQuestionnaireDataByJob')

    localStorage.removeItem("baseJobListingData")
    localStorage.removeItem("activeFiltersTopics");
    localStorage.removeItem('cachedJobsListData')
    localStorage.removeItem('baseJobScoreCardData')
    localStorage.removeItem('baseScoreCardDataByJob')
    localStorage.removeItem('baseQuestionnaireDataByJob')
    localStorage.removeItem('jobPipelineStageData')
    localStorage.removeItem('activeFiltersJobApplication')
    localStorage.removeItem('activeFiltersJobsListing')
    localStorage.removeItem('activeFiltersJobsDetailsPage')
    localStorage.removeItem('activeFiltersJobApplicationKanban')
    localStorage.removeItem('jobByHiringTeamMembers')
    localStorage.removeItem('jobActionData')
    localStorage.removeItem('jobByReviewData')
    localStorage.removeItem('jobPostDetailsData')
    localStorage.removeItem('JobPipelineDetailsData')
    localStorage.removeItem('forgot_email')
    try {

      const baseResponse = await dispatch(fetchDocument({
        baseId,
        id: item.id // Use item.id instead of jobId
      }));

      if (baseResponse?.payload?.data) {
        localStorage.setItem('jobPostDetailsData', JSON.stringify(baseResponse.payload.data));
      }


    } catch (error) {
      console.error("Error fetching document details:", error);
      // You might want to show an error toast here
    }
  };


  const handleHelpSearchClick = (searchValue) => {
    setSearchTerm(searchValue);
    closeModal();
    if (baseId) {
      navigate(`/${baseId}/jobs`);
    }
  };

  return (
    <div className="w-full max-w-xl absolute left-[50%] mt-0" style={{ transform: 'translate(-50%, 0)' }}>
      <div className="relative cursor-pointer" onClick={openModal}>
        <Search className="w-4 h-4 text-gray-500 pointer-events-none absolute inset-y-0 left-3 my-auto" />
        <div className="w-full pl-10 border border-gray-300 bg-white rounded-lg py-2 px-4 text-sm h-9 flex items-center justify-between">
          <span className="text-gray-500">Search jobs...</span>
          <div className="flex items-center gap-1 bg-gray-100 px-2 py-1 rounded text-xs text-gray-500">
            <Command className="w-3 h-3" />
            <span>K</span>
          </div>
        </div>
      </div>

      {/* Search Dialog */}
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={closeModal} initialFocus={searchInputRef}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 " />
          </Transition.Child>

          <div className=" top-[-8px] mq1800:left-[50%] fixed mq3000:left-[50%] overflow-y-auto max-w-[608px] w-full"
            style={{ transform: 'translate(-50%, 0)' }}
          >

            <div className="flex min-h-full p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel ref={dialogRef} className="w-full max-w-xl transform overflow-hidden rounded-lg bg-white transition-all  search-shadow">
                  <div className="relative">
                    <Combobox value={null} onChange={(item) => {
                      if (item) {
                        if (item.type === 'help-search') {
                          // Handle the "Search in application" option
                          handleHelpSearchClick(item.query);
                        } else {
                          handleNavigate(item);
                        }
                      }
                    }}>
                      <div className="relative">
                        {isDebouncedLoading ? (
                          <div className="absolute top-[15px] left-3 my-auto">
                            <div className="simple-spinner-loader">
                              <span className='!w-4 !h-4'></span>
                            </div>
                          </div>
                        ) : (
                          <Search className="w-4 h-4 text-gray-500 pointer-events-none absolute inset-y-0 left-3 my-auto" />
                        )}
                        <Combobox.Input
                          ref={searchInputRef}
                          className="w-full pl-10 pr-10 border-b border-gray-200 py-3 px-4 rounded-t-lg text-sm focus:outline-none focus:ring-0 focus-within:ring-0 focus-visible:ring-0 focus:border-t-0 focus:border-l-0 focus:border-r-0 focus:border-b-gray-200"
                          placeholder="Search jobs..."
                          value={query}

                          onChange={handleQueryChange}
                        />
                        <button
                          onClick={closeModal}
                          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-gray-100"
                        >
                          <X className="w-4 h-4 text-gray-500" />
                        </button>
                      </div>

                      <Combobox.Options static className="max-h-80 overflow-y-auto pt-0 px-0">
                        {isLoading ? (
                          <div className="flex items-center justify-center w-full h-[calc(62vh_-_220px)]">
                            <div className="simple-spinner-loader">
                              <span></span>
                            </div>
                          </div>
                        ) : filteredItems.length > 0 ? (
                          <div className='px-2'>
                            {filteredItems.map((item) => (

                              <Combobox.Option
                                key={item.id}
                                value={item}
                                className={({ active }) =>
                                  `cursor-pointer first-of-type:mt-1 last-of-type:mb-1 select-none px-3 rounded-lg py-3 ${active ? 'bg-gray-100' : ''}`
                                }
                              >
                                {({ active }) => (
                                  <div className='flex flex-col gap-y-[2px]'>
                                    <div className='flex justify-between items-center'>
                                      <div className='flex items-center justify-between w-full'>
                                        <div className="text-[#1b1b1b] text-[13px] font-normal leading-4">{item.label}</div>
                                        <div className=' gap-[6px]  text-[13px]  capitalize inline-flex items-center rounded-md bg-gray-50 px-2 py-1 
                                       text-xs font-medium text-gray-600 ring-1 ring-inset ring-gray-500/10  w-fit'>
                                          {item.status}
                                        </div>

                                      </div>



                                      <div
                                        className='flex gap-x-1 items-center rounded-md px-2 py-1 text-xs font-medium text-gray-600 border-solid w-fit'
                                        style={{
                                          backgroundColor: item.stageColor ? `${item.stageColor}1A` : 'bg-gray-50',
                                          color: item.stageColor || 'text-gray-600',
                                        }}
                                      >
                                        {item.tags}
                                      </div>
                                    </div>
                                    <div className="flex justify-start items-center gap-x-2">
                                      {
                                        item.refId && (
                                          <span className="text-gray-400 text-[12px] font-normal leading-4">
                                            #{item.refId}
                                          </span>
                                        )
                                      }
                                      {item.experience_level && (
                                        <div className='flex items-center gap-x-2'>
                                          <div className='h-1 w-1 bg-[#030b3e5c] rounded-xl'></div>
                                          <span className="text-gray-400 text-[12px] font-normal leading-4">

                                            {item.experience_level}
                                          </span>
                                        </div>
                                      )}
                                      {item.job_type && (
                                        <div className='flex items-center gap-x-2'>
                                          <div className='h-1 w-1 bg-[#030b3e5c] rounded-xl'></div>
                                          <span className="text-gray-400 text-[12px] font-normal leading-4">
                                            {item.job_type}
                                          </span>
                                        </div>
                                      )}




                                    </div>
                                  </div>
                                )}
                              </Combobox.Option>
                            ))}

                            {/* {!query && !showMore && searchItems.length > 3 && (
                              <div className="px-4 py-2">
                                <button
                                  className="text-blue-600 text-sm hover:underline"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    setShowMore(true);
                                  }}
                                >
                                  Show {searchItems.length - 3} more
                                </button>
                              </div>
                            )} */}
                          </div>
                        ) : (
                          <div className="px-4 py-2 h-[calc(62vh_-_220px)] flex items-center justify-center text-center text-sm text-gray-500">
                            No jobs found for "{query}"
                          </div>
                        )}

                        {query && (
                          <div className='bg-[#f3f3f3] pt-2 pb-2 mt-1 '>
                            {/* <Combobox.Option
                              value={{ type: 'shopify-search', query }}
                              className={({ active }) =>
                                `flex items-center px-3 rounded-lg py-3 cursor-pointer ${active ? 'bg-gray-100' : ''}`
                              }
                            >
                              <Search className="w-4 h-4 mr-2 text-gray-500" />
                              <span>Search "{query}" in Shopify App Store</span>
                            </Combobox.Option> */}
                            <Combobox.Option
                              value={{ type: 'help-search', query }}
                              className={({ active }) =>
                                `flex items-center px-3 mx-2 rounded-lg py-2 cursor-pointer ${active ? 'bg-gray-200' : 'bg-gray-200'}`
                              }
                            >
                              <Search className="w-4 h-4 mr-2 text-gray-500" />
                              <span>Search "{query}" in jobs listing</span>
                            </Combobox.Option>
                          </div>
                        )}
                      </Combobox.Options>
                    </Combobox>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
}